import { useEffect } from "react";
import { useSignalR } from "./SignalRContext";
import { HubConnectionState } from '@microsoft/signalr';

export const SignalRGlobalListeners = () => {
  const {
    accountingConnection,
    hospitalityConnection,
    crmConnection,
    deliverableConnection,
  } = useSignalR();

  const setupHubListeners = (connection: any, prefix: string) => {
    let intervalId: NodeJS.Timeout | null = null;
    let channel: BroadcastChannel | null = null;

    const registerHandlers = (rawConnection: any) => {
      console.log(`🔧 [${prefix}] Registering handlers for:`, rawConnection);

      const methods = [
        "roomcategorycreated",
        "roomcategoryupdated",
        "roomcategorydeleted",
        "amenitycreated",  // Lowercase
        "amenityupdated",  // Lowercase
        "amenitydeleted",  // Lowercase
        "roomcreated",
        "roomupdated",
        "roomdeleted",
      ];

      methods.forEach((methodName) => {
        rawConnection.on(methodName, (payload: any) => {
          console.log(`🧭 [${prefix}] ${methodName} received:`, payload);

          const type = prefix === "hospitality" ? methodName : `${prefix}_${methodName}`;
          channel!.postMessage({ type, payload });

          console.log(`📤 [${prefix}] BroadcastChannel posted → ${type}:`, payload);
        });
      });
    };

    const setupHandlers = () => {
      const rawConnection = connection?.connection;
      if (!rawConnection) {
        console.warn(`[${prefix}] No raw connection available`);
        return;
      }

      if (rawConnection.state === HubConnectionState.Disconnected) {
        console.log(`[${prefix}] Starting connection manually`);
        rawConnection.start()
          .then(() => {
            console.log(`✅ [${prefix}] Connected after manual start`);
          })
          .catch((err: any) => {
            console.error(`[${prefix}] Start error:`, err);
          });
        return; // Wait for next interval to check again
      }

      if (rawConnection.state !== HubConnectionState.Connected) {
        console.warn(`[${prefix}] Connection not ready. Current state: ${rawConnection.state}`);
        return;
      }

      if (channel) {
        console.log(`[${prefix}] Handlers already set up - skipping duplicate setup`);
        return;
      }

      channel = new BroadcastChannel("signalr-events");

      registerHandlers(rawConnection);

      const handleBroadcast = (event: MessageEvent) => {
        console.log(`📨 [${prefix}] Cross-tab event received → ${event.data.type}:`, event.data.payload);
      };

      channel.addEventListener("message", handleBroadcast);

      rawConnection.onreconnected(() => {
        registerHandlers(rawConnection);
        console.log(`[${prefix}] Handlers re-registered after reconnect`);
      });

      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }

      console.log(`✅ [${prefix}] Listeners attached`);
    };

    // Immediate setup attempt (fix for delay)
    setupHandlers();

    intervalId = setInterval(setupHandlers, 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (channel) {
        channel.close();
        console.log(`❌ [${prefix}] BroadcastChannel closed`);
      }

      const rawConnection = connection?.connection;
      if (rawConnection) {
        const methods = [
          "roomcategorycreated",
          "roomcategoryupdated",
          "roomcategorydeleted",
          "amenitycreated",  // Lowercase
          "amenityupdated",  // Lowercase
          "amenitydeleted",  // Lowercase
          "roomcreated",
          "roomupdated",
          "roomdeleted",
        ];
        methods.forEach((methodName) => rawConnection.off(methodName));
      }

      console.log(`❌ [${prefix}] Cleaned up listeners`);
    };
  };

  useEffect(() => {
    const cleanup = setupHubListeners(accountingConnection, "accounting");
    return cleanup;
  }, [accountingConnection]);

  useEffect(() => {
    const cleanup = setupHubListeners(hospitalityConnection, "hospitality");
    return cleanup;
  }, [hospitalityConnection]);

  useEffect(() => {
    const cleanup = setupHubListeners(crmConnection, "crm");
    return cleanup;
  }, [crmConnection]);

  useEffect(() => {
    const cleanup = setupHubListeners(deliverableConnection, "deliverable");
    return cleanup;
  }, [deliverableConnection]);

  return null;
};